import React, { useEffect, useRef, useState } from 'react';
import {
  widget,
  WidgetDataType,
  useSearchResultsActions,
  EntityModel,
  SearchResponseFacet,
  useSearchResultsSelectedFilters,
  FilterNot,
  FilterEqual,
  FilterAnd,
} from '@sitecore-search/react';
import classNames from 'classnames';
import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { AccordionFacets, FacetItem, SearchResultsAccordionFacets } from '@sitecore-search/ui';
import { tv } from 'tailwind-variants';

import { Resources } from 'lib/templates/Feature.BOKF.model';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import SVG from 'helpers/SVG/SVG';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import Button from 'helpers/Button/Button';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import { formatTimestamp } from 'lib/utils/date-utils';
import {
  getAllFacets,
  searchSourceIds,
  getProcessedFacets,
  ProcessedFacetItem,
  getIconDetailsFromFacetId,
} from './SearchWithFilter.utils';
import { useSanitizedSearchResults } from 'lib/utils/token-replacer';
import { FacetValue } from 'lib/utils/use-ensure-facet-url';

export type CardProps = {
  isCategoryHidden?: boolean;
  ribbonLabel?: Field<string>;
  article_time?: number;
  category?: Array<string>;
  category_color?: Array<'category-1' | 'category-2' | 'category-3' | 'category-4'>;
  city?: Array<string>;
  description?: string;
  id?: string;
  image_url?: string;
  media_url?: string;
  name?: string;
  publish_date?: string;
  url?: string;
  type?: 'Article' | 'VideoResource';
};

interface SearchFilterConfiguration {
  id?: string;
  name?: string;
  fields: {
    InitialCountDesktop?: Field<number>;
    InitialCountMobile?: Field<number>;
    LoadMoreCount?: Field<number>;
    LoadMoreText?: Field<string>;
    NoResultsText?: Field<string>;
  };
}

interface WidgetComponentProps {
  rfkId: string;
  InitialCount: number;
}

type SearchWithFilterWidgetProps = Required<SearchFilterConfiguration> & WidgetComponentProps;

interface SearchWithFilterProps
  extends Required<Resources.Configuration.SearchFilterConfiguration> {
  InitialCount: number;
}

const SearchWithFilter = ({ fields, InitialCount }: SearchWithFilterProps) => {
  const { currentScreenWidth, isScreenTypeDetected } = useCurrentScreenType();

  const {
    LoadMoreCount = { value: 4 },
    LoadMoreText = { value: 'View More' },
    ShowFilter = { value: 'Show Filter' },
    HideFilter = { value: 'Hide Filter' },
    PanelFilter = { value: 'Filter' },
    CategoryLabel = { value: 'Category' },
    TopicsLabel = { value: 'Topic' },
    TypesLabel = { value: 'Type' },
    TopicsSearchBoxPlaceholder = { value: 'Search topics' },
    TopicsNotFoundMessage = { value: 'No topic found. Please try again.' },
    TopicsToExclude = [],
    TypesToExclude = [],
    ClearAllPanel = { value: 'Clear All' },
    ClearAllChips = { value: 'Clear All' },
    NoResultsText = { value: 'No Results' },
    ViewResults = { value: 'View Results' },
    TopicsSearchBoxAriaLabel = { value: 'Topic Searchbox' },
    TopicsSearchBoxButtonAriaLabel = { value: 'Topic Search Button' },
    TopicsSearchBoxClearAriaLabel = { value: 'Clear Topic Searchbox' },
    TopicsSkipFilter = { value: 'Skip Topic Filter' },
  } = fields;

  const [allFacets, setAllFacets] = useState<Array<SearchResponseFacet>>([]);

  useEffect(() => {
    (async () => {
      // Get all facets on load
      const _allFacets = await getAllFacets(TopicsToExclude, TypesToExclude);
      setAllFacets(_allFacets);
    })();
  }, []);

  // States
  const [expertsData, setExpertsData] = useState<{
    expertList: Array<EntityModel>;
    fetchCount: number;
  }>({ expertList: [], fetchCount: 1 });

  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);

  const selectedFacetsCountRef = useRef<number>(0);

  const {
    sanitizedResults,
    widgetRef,
    query,
    state: { selectedFacets },
    queryResult: {
      isInitialLoading,
      isFetched,
      data: { total_item = 0, limit = 0, offset = 0, facet: enabledFacets = [] } = {},
    },
  } = useSanitizedSearchResults({
    query: (query) =>
      query
        .getRequest()
        .setSearchFacetAll(false)
        .setSearchFacetTypes([{ name: 'category' }, { name: 'topics' }, { name: 'type' }])
        .setSearchFilter(
          new FilterAnd([
            ...TopicsToExclude.map(
              (topic) =>
                new FilterNot(new FilterEqual('topics', (topic.fields.Name as Field<string>).value))
            ),
            ...TypesToExclude.map(
              (type) =>
                new FilterNot(
                  new FilterEqual(
                    'type',
                    (type.fields.Name as Field<string>).value?.toLowerCase() == 'video'
                      ? 'VideoResource'
                      : (type.fields.Name as Field<string>).value?.toLowerCase() == 'calculator'
                      ? 'CalculatorResource'
                      : (type.fields.Name as Field<string>).value
                  )
                )
            ),
          ])
        )
        .setSources(searchSourceIds),
    config: {
      defaultFacetType: 'text',
    },
    state: {
      itemsPerPage: InitialCount,
      page: 1,
      selectedFacets: (() => {
        const hasWindow = typeof window !== 'undefined';
        const preselectedFacets: Array<FacetValue> = [];
        if (hasWindow) {
          const currentURL = new URL(window.location.href);
          const searchParams = new URLSearchParams(currentURL.search);

          for (const [key, value] of searchParams.entries()) {
            // Ignore if facet is not equal to below types
            if (key !== 'category' && key !== 'topics' && key !== 'type') {
              continue;
            }

            value.split(',').forEach((facetText) => {
              preselectedFacets.push({
                facetId: key,
                facetValueText: facetText,
              });
            });
          }
        }
        return preselectedFacets;
      })(),
    },
  });

  // Handle the scroll enable/disable functionality on filter panel visibility
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      document &&
      currentScreenWidth <= getBreakpoint('desktop')
    ) {
      if (isFilterPanelVisible) {
        // Calculate scrollbar width if present to handle the layout shift
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.paddingRight = ''; // Stop scrolling
        document.body.style.overflow = 'auto'; // Allow scrolling
      }
    }
    // Cleanup function
    return () => {
      document.body.style.paddingRight = ''; // Stop scrolling
      document.body.style.overflow = 'auto'; // Ensure overflow is reset
    };
  }, [isFilterPanelVisible, currentScreenWidth]);

  useEffect(() => {
    if (!isInitialLoading && sanitizedResults?.content) {
      // Handle facet selection and search input change
      if (
        offset === 0 ||
        (selectedFacets && selectedFacets.length !== selectedFacetsCountRef.current)
      ) {
        selectedFacetsCountRef.current = selectedFacets?.length || 0;
        setExpertsData({
          fetchCount: 1,
          expertList: sanitizedResults.content,
        });
      }
      //Handle load more
      else
        setExpertsData({
          fetchCount: expertsData.fetchCount + 1,
          expertList: [...expertsData.expertList, ...sanitizedResults.content],
        });
    }
    // We only need to care about experts and offset as a dependency
    // because load more pagination requires new results to be loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizedResults?.content]);

  const loadMoreResults = () => {
    const _loadCount = Number(LoadMoreCount.value);

    if (expertsData.fetchCount >= 1) {
      query.getRequest().setSearchLimit(_loadCount).setSearchOffset(expertsData.expertList.length);
    }
  };

  if (!isScreenTypeDetected) return <></>;

  return (
    <section className="component container spacing-md" ref={widgetRef}>
      <div className="flex flex-col gap-4 md:gap-6">
        <div
          tabIndex={0}
          className={classNames(
            'group max-w-fit p-2 flex items-center justify-center gap-2 cursor-pointer',
            'border border-transparent rounded-[4px]',
            'hover:bg-light-background-neutral-highest focus:outline-none focus:border-light-stroke-primary-variant',
            isFilterPanelVisible && 'bg-light-background-neutral-highest'
          )}
          onClick={() => setIsFilterPanelVisible(!isFilterPanelVisible)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsFilterPanelVisible(!isFilterPanelVisible);
            }
          }}
        >
          <SVG
            className="[&_svg]:w-4 [&_svg]:h-4 [&_svg]:md:w-5 [&_svg]:md:h-5 [&_svg]:lg:w-6 [&_svg]:lg:h-6 [&_path]:fill-light-fill-secondary"
            svg="icon-sort-filter"
          />
          <p className="p2-medium text-light-fill-secondary group-hover:underline">
            {isFilterPanelVisible ? HideFilter.value : ShowFilter.value}
          </p>
        </div>
        {isFilterPanelVisible && (
          // Transparent Overlay
          <div
            className="md:hidden fixed w-screen h-lvh top-0 left-0 z-40"
            onClick={() => setIsFilterPanelVisible(false)}
          />
        )}
        <div
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setIsFilterPanelVisible(false);
            }
          }}
          className={classNames(
            'fixed md:static flex md:hidden right-0 top-0 ease-in-out duration-100 items-start flex-col md:flex-row gap-2 bg-light-background-neutral z-50 md:z-0 shadow-large md:shadow-none',
            isFilterPanelVisible ? 'translate-x-0 md:!flex' : 'translate-x-full md!:translate-x-0'
          )}
        >
          <FilterPanel
            allFacets={allFacets}
            enabledFacets={enabledFacets}
            labels={{
              panelTitle: PanelFilter.value,
              searchBoxPlaceholder: TopicsSearchBoxPlaceholder.value,
              noTopicsFoundMessage: TopicsNotFoundMessage.value,
              category: CategoryLabel.value,
              topics: TopicsLabel.value,
              type: TypesLabel.value,
              clearAll: ClearAllPanel.value,
              searchboxAria: TopicsSearchBoxAriaLabel.value,
              searchboxButtonAria: TopicsSearchBoxButtonAriaLabel.value,
              searchboxClearAria: TopicsSearchBoxClearAriaLabel.value,
              topicsSkipFilter: TopicsSkipFilter.value,
              viewResults: ViewResults.value,
            }}
            setIsFilterPanelVisible={setIsFilterPanelVisible}
          />
        </div>

        <FilterChips clearAllLabel={ClearAllChips.value} />

        {total_item > 0 && expertsData.expertList.length > 0 && (
          <ul className="flex flex-wrap flex-col md:flex-row gap-6 md:gap-4 mt-2 md:mt-4">
            {expertsData.expertList.map((expertDetails, i) => (
              <li
                className="basis-full sm:basis-[calc(100%_/_2_-_16px_/_2_*_(2_-_1))] lg:basis-[calc(100%_/_3_-_16px_/_3_*_(3_-_1))]"
                key={i}
              >
                <Card {...expertDetails} />
              </li>
            ))}
          </ul>
        )}

        {/* No Results */}
        {isFetched && total_item === 0 && (
          <p className="p2-medium text-light-fill-primary-variant">{NoResultsText.value}</p>
        )}

        <div className="flex items-center justify-center mt-10 sm:mt-8">
          <Button
            disabled={limit + offset === total_item}
            label={LoadMoreText.value}
            type="auxiliary"
            fieldClass="!rounded-[4px] !w-auto focus:border-light-stroke-primary-variant-highest"
            onClick={loadMoreResults}
            onKeyDown={(e) => e.key === 'Enter' && loadMoreResults()}
          />
        </div>
      </div>
    </section>
  );
};

//#region Filter Chips
const FilterChips = ({ clearAllLabel }: { clearAllLabel: string }) => {
  const selectedFacetsFromApi = useSearchResultsSelectedFilters();
  const { onRemoveFilter, onClearFilters } = useSearchResultsActions();

  if (selectedFacetsFromApi.length === 0) return <></>;

  return (
    <ul className="flex flex-wrap gap-3 md:gap-4 items-center justify-start">
      {selectedFacetsFromApi?.map((_selectedFacet, index) => {
        const selectedFacet = _selectedFacet as typeof _selectedFacet & { facetValueId: string };

        return (
          <li
            tabIndex={0}
            className={classNames(
              'inline-flex items-center justify-center gap-2 p-2 rounded-[4px] cursor-pointer p3',
              'border border-light-stroke-primary-variant hover:border-light-stroke-primary-variant-high hover:underline',
              'focus:outline-none focus:border-light-stroke-primary-variant-highest focus:p3-medium'
            )}
            key={index}
            onClick={() => onRemoveFilter(selectedFacet)}
            onKeyDown={(e) => e.key === 'Enter' && onRemoveFilter(selectedFacet)}
          >
            {getIconDetailsFromFacetId[selectedFacet.facetValueId] && (
              <div className="relative w-4 h-4 md:w-6 md:h-6">
                <SVG
                  svg={`icon-${getIconDetailsFromFacetId[selectedFacet.facetValueId].name}`}
                  className={classNames(
                    getIconDetailsFromFacetId[selectedFacet.facetValueId].color ||
                      'text-light-fill-secondary',
                    'group-data-[state=selected]:text-light-fill-inverse [&_svg]:p-0.5 [&_svg]:absolute [&_svg]:top-0'
                  )}
                />
              </div>
            )}
            {selectedFacet.valueLabel?.toLowerCase() == 'videoresource'
              ? 'Video'
              : selectedFacet.valueLabel?.toLowerCase() == 'calculatorresource'
              ? 'Calculator'
              : selectedFacet.valueLabel}
            <SVG className="[&_svg]:w-3 [&_svg]:h-3 stroke-2" svg="icon-close-thick" />
          </li>
        );
      })}
      {selectedFacetsFromApi.length > 0 && (
        <li
          tabIndex={0}
          className="cursor-pointer p3-medium text-light-fill-secondary"
          onClick={() => onClearFilters()}
          onKeyDown={(e) => e.key === 'Enter' && onClearFilters()}
        >
          {clearAllLabel}
        </li>
      )}
    </ul>
  );
};
//#endregion

//#region Filter Panel
type LabelTypes =
  | 'panelTitle'
  | 'topics'
  | 'type'
  | 'category'
  | 'searchBoxPlaceholder'
  | 'noTopicsFoundMessage'
  | 'clearAll'
  | 'viewResults'
  | 'searchboxAria'
  | 'searchboxButtonAria'
  | 'searchboxClearAria'
  | 'topicsSkipFilter';

type Labels = Record<LabelTypes, string>;

const FilterPanel = ({
  allFacets,
  enabledFacets,
  setIsFilterPanelVisible,
  labels,
}: {
  allFacets: Array<SearchResponseFacet>;
  enabledFacets: Array<SearchResponseFacet>;
  setIsFilterPanelVisible: React.Dispatch<boolean>;
  labels: Labels;
}) => {
  const { onFacetClick, onClearFilters } = useSearchResultsActions();
  const processedFacets = getProcessedFacets(allFacets, enabledFacets);
  const { currentScreenWidth } = useCurrentScreenType();

  const [searchBoxValue, setSearchBoxValue] = useState('');
  const topicsListRef = useRef<HTMLDivElement>(null);

  const handleTopicsMaskVisibility = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    if (topicsListRef.current && currentScreenWidth > getBreakpoint('desktop')) {
      const topicsListElement = e.currentTarget;

      if (topicsListElement.children.length > 0) {
        const topicsListElementYAxis = topicsListElement.getBoundingClientRect();
        const firstTopicElementYAxis =
          topicsListElement.firstElementChild?.getBoundingClientRect().y;
        const lastTopicElementYAxis = topicsListElement.lastElementChild?.getBoundingClientRect().y;

        const beforeElementVisibileClass = 'md:before:inline-block';
        const afterElementVisibileClass = 'md:after:inline-block';

        // Toggle topics top mask
        if (firstTopicElementYAxis && firstTopicElementYAxis < topicsListElementYAxis.top) {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          !topicsListRef.current.classList.contains(beforeElementVisibileClass) &&
            topicsListRef.current.classList.add(beforeElementVisibileClass);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          topicsListRef.current.classList.contains(beforeElementVisibileClass) &&
            topicsListRef.current.classList.remove(beforeElementVisibileClass);
        }

        // Toggle topics bottom mask
        if (lastTopicElementYAxis && lastTopicElementYAxis > topicsListElementYAxis.bottom - 24) {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          !topicsListRef.current.classList.contains(afterElementVisibileClass) &&
            topicsListRef.current.classList.add(afterElementVisibileClass);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          topicsListRef.current.classList.contains(afterElementVisibileClass) &&
            topicsListRef.current.classList.remove(afterElementVisibileClass);
        }
      }
    }
  };

  const handleKeyDownOnFacets = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const getNewFacetIndex = (isElementDisable: boolean) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          if (currentFocusedFacetIndex < facetListElements.length - 1 && !isElementDisable)
            return ++currentFocusedFacetIndex;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          if (currentFocusedFacetIndex > 0 && !isElementDisable) return --currentFocusedFacetIndex;
          break;
      }

      return currentFocusedFacetIndex;
    };

    const facetListElements = e.currentTarget.children as unknown as Array<HTMLLIElement>;
    const currentFocusedFacetElement = e.target as unknown as HTMLButtonElement;
    let currentFocusedFacetIndex = parseInt(currentFocusedFacetElement.dataset.uiIndex!);
    const ariaDisabled: string | null =
      currentFocusedFacetElement?.attributes?.getNamedItem('aria-disabled')?.value || null;
    const isElementDisable = ariaDisabled == 'false' && ariaDisabled != null ? false : true;
    try {
      if (e.key === 'Enter') {
        (
          facetListElements[getNewFacetIndex(isElementDisable)].children[0] as HTMLButtonElement
        ).click();
        return;
      }

      if (
        e.key === 'ArrowDown' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowLeft'
      ) {
        e.preventDefault();
        (
          facetListElements[getNewFacetIndex(isElementDisable)].children[0] as HTMLButtonElement
        ).focus();
      }
    } catch (error) {
      console.error('Error while navigating through facets using arrow keys', error);
    }
  };

  const renderTopicsFacet = (topicsFacet: SearchResponseFacet) => {
    const topicsToRender = !searchBoxValue
      ? topicsFacet.value
      : topicsFacet.value.filter((facet) =>
          facet.text.toLowerCase().includes(searchBoxValue.toLowerCase())
        );

    if (topicsToRender.length === 0)
      return (
        <Text tag="p" field={{ value: labels.noTopicsFoundMessage }} className="p2 text-center" />
      );
    return (topicsToRender as Array<ProcessedFacetItem>)
      .sort((a, b) => {
        // First sort by enabled status (enabled items first)
        const enabledComparison = Number(b.isEnabled) - Number(a.isEnabled);
        if (enabledComparison !== 0) return enabledComparison;

        // Then sort alphabetically by text
        return a.text.localeCompare(b.text);
      })
      .map((v, index: number) => (
        <FacetItem
          key={v.id}
          {...{
            index,
            facetValueId: v.id,
          }}
          className={classNames(
            'group flex items-center cursor-pointer gap-2',
            'text-light-fill-primary-variant data-[state=selected]:text-light-fill-primary',
            'p2 data-[state=selected]:p2-medium',
            !v.isEnabled && 'pointer-events-none'
          )}
          aria-disabled={!v.isEnabled}
        >
          <AccordionFacets.ItemCheckbox
            className={classNames(
              'peer relative flex-none rounded-[1px] border-2 border-light-stroke-primary-variant cursor-pointer',
              'w-6 h-6',
              'focus:border-light-stroke-primary-variant-highest',
              'aria-checked:bg-light-background-primary aria-checked:hover:border-light-stroke-primary-variant-high aria-checked:hover:bg-light-background-primary-high',
              'aria-checked:focus:bg-light-background-primary-highest focus:outline-none focus:border-light-stroke-primary-variant-highest',
              'group-aria-disabled:!border-light-stroke-primary-low'
            )}
            tabIndex={v.isEnabled ? 0 : -1}
            aria-label={v.text}
            data-ui-index={index}
            aria-disabled={!v.isEnabled ? true : false}
          >
            <AccordionFacets.ItemCheckboxIndicator>
              <SVG
                className="[&_svg]:p-0.5 [&_svg]:absolute [&_svg]:top-0 [&_path]:fill-light-fill-inverse"
                svg="icon-thin-check"
              />
            </AccordionFacets.ItemCheckboxIndicator>
          </AccordionFacets.ItemCheckbox>
          <AccordionFacets.ItemLabel className="p2 group-aria-disabled:!text-light-fill-primary-low">
            {v.text}
          </AccordionFacets.ItemLabel>
        </FacetItem>
      ));
  };

  return (
    <div className="relative flex flex-col h-lvh md:h-full max-w-[80vw] md:max-w-full md:w-full md:border-b md:border-light-stroke-primary">
      <div className="flex-grow p-6 sm:p-8 md:p-0 flex flex-col md:flex-row gap-8 overflow-y-auto md:overflow-y-visible">
        <div className="md:hidden flex items-center gap-4 self-stretch w-full justify-between">
          <Text tag="h4" field={{ value: labels.panelTitle }} />
          <div tabIndex={0} onClick={() => setIsFilterPanelVisible(false)}>
            <SVG className="[&_svg]:w-4 [&_svg]:h-4 cursor-pointer" svg="icon-close" />
          </div>
        </div>

        {/* Topics Facets */}
        <SearchResultsAccordionFacets
          defaultFacetTypesExpandedList={['category', 'topics', 'type']}
          onFacetValueClick={onFacetClick}
          className="flex flex-col md:flex-row w-full gap-8"
        >
          {processedFacets.map((processedFacet) => {
            return (
              <AccordionFacets.Facet
                facetId={processedFacet.name}
                key={processedFacet.name}
                className={classNames(
                  processedFacet.name === 'topics'
                    ? 'order-3 md:order-2 gap-2 sm:gap-4'
                    : processedFacet.name === 'type' && 'order-2 md:order-3',
                  processedFacet.name !== 'topics' && 'flex-grow',
                  'flex flex-col gap-4'
                )}
              >
                <AccordionFacets.Header
                  asChild
                  className="bg-light-background-neutral pointer-events-none"
                >
                  <p
                    tabIndex={0}
                    id={processedFacet.name}
                    className="text-left p2-medium scroll-mt-4"
                  >
                    {labels[processedFacet.name as LabelTypes] || processedFacet.name}
                  </p>
                </AccordionFacets.Header>
                {processedFacet.name === 'topics' ? (
                  <>
                    <div className="flex items-start justify-center min-h-[44px] md:min-w-[352px]">
                      <div className="relative flex-grow self-stretch">
                        <input
                          onChange={(e) => setSearchBoxValue(e.target.value)}
                          value={searchBoxValue}
                          className={classNames(
                            'border border-light-stroke-primary border-r-0 rounded-l-[28px] pl-4 w-full h-full focus:outline-none focus:border-light-stroke-primary-variant',
                            searchBoxValue ? 'p3-medium' : 'p3'
                          )}
                          placeholder={labels.searchBoxPlaceholder}
                          aria-label={labels.searchboxAria}
                        />
                        {/* Clear Button */}
                        {searchBoxValue && (
                          <button
                            tabIndex={0}
                            className="absolute right-[14px] top-1/2 -translate-y-1/2"
                            onClick={() => setSearchBoxValue('')}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setSearchBoxValue('');
                              }
                            }}
                            aria-label={labels.searchboxClearAria}
                          >
                            <SVG
                              className="inline-block [&_svg]:w-3 [&_svg]:h-3 text-light-fill-primary-variant"
                              svg="icon-close"
                            />
                          </button>
                        )}
                      </div>
                      {/* Search Button */}
                      <button
                        tabIndex={-1}
                        className="flex items-center justify-center self-stretch px-[12px] rounded-r-[28px] bg-light-background-gradient hover:bg-light-background-gradient-hover focus:bg-light-background-gradient-focus outline-none focus:outline-none"
                        aria-label={labels.searchboxButtonAria}
                      >
                        <SVG
                          className="[&_svg]:h-5 [&_svg]:w-5 [&_path]:stroke-light-fill-inverse"
                          svg="icon-search2"
                        />
                      </button>
                    </div>
                    <Button
                      tabIndex={0}
                      field={{ value: { href: '#type', text: labels.topicsSkipFilter } }}
                      fieldClass={classNames(
                        'absolute opacity-0 pointer-events-none bg-transparent bottom-0 right-0',
                        'focus:static focus:opacity-100 focus:pointer-events-auto focus:border-light-stroke-primary-variant-highest'
                      )}
                      size="full"
                      type="auxiliary"
                    />
                    <AccordionFacets.Content
                      ref={topicsListRef}
                      className={classNames(
                        'relative flex flex-col flex-1 px-2 sm:px-4',
                        'before:hidden before:absolute before:top-0 before:left-0 before:w-full before:h-6 before:bg-light-background-gradient-gray',
                        'after:hidden after:absolute after:bottom-0 after:left-0 after:w-full after:h-6 after:bg-light-background-gradient-gray'
                      )}
                    >
                      <AccordionFacets.ValueList
                        onScroll={(e) => handleTopicsMaskVisibility(e)}
                        onKeyDown={(e) => handleKeyDownOnFacets(e)}
                        className="group list-none flex flex-col gap-2 md:max-h-[262px] overflow-y-auto md:pb-4"
                      >
                        {renderTopicsFacet(processedFacet)}
                      </AccordionFacets.ValueList>
                    </AccordionFacets.Content>
                  </>
                ) : (
                  <AccordionFacets.Content className="flex flex-col flex-1 overflow-y-auto md:pb-4">
                    <AccordionFacets.ValueList
                      className="list-none flex flex-wrap gap-3"
                      onKeyDown={(e) => handleKeyDownOnFacets(e)}
                    >
                      {processedFacet.value.map((v: ProcessedFacetItem, index: number) => (
                        <FacetItem
                          key={v.id}
                          {...{
                            index,
                            facetValueId: v.id,
                          }}
                          className="group"
                          aria-disabled={!v.isEnabled}
                        >
                          <AccordionFacets.ItemCheckbox
                            tabIndex={v.isEnabled ? 0 : -1}
                            className="peer absolute opacity-0"
                            aria-label={v.text}
                            data-ui-index={index}
                          ></AccordionFacets.ItemCheckbox>
                          <AccordionFacets.ItemLabel
                            className={classNames(
                              'flex items-center justify-center gap-2 p-2 rounded border p3 cursor-pointer',
                              'border-light-stroke-primary-variant hover:border-light-stroke-primary-variant-high peer-focus:border-light-stroke-primary-variant-highest peer-focus:p3-medium',
                              'hover:underline peer-focus:outline-none',
                              'group-data-[state=selected]:bg-light-background-primary group-data-[state=selected]:text-light-fill-inverse group-data-[state=selected]:p3-medium group-data-[state=selected]:hover:bg-light-background-primary-high group-data-[state=selected]:focus:bg-light-background-primary-highest',
                              'peer-data-[state=checked]:peer-focus:bg-light-background-primary-highest',
                              !v.isEnabled &&
                                'pointer-events-none !border-light-stroke-primary-lowest !bg-transparent !text-light-fill-primary-low !p3'
                            )}
                          >
                            {getIconDetailsFromFacetId[v.id] && (
                              <div className="relative w-4 h-4 md:w-6 md:h-6">
                                <SVG
                                  svg={`icon-${getIconDetailsFromFacetId[v.id].name}`}
                                  className={classNames(
                                    getIconDetailsFromFacetId[v.id].color ||
                                      'text-light-fill-secondary',
                                    'group-data-[state=selected]:text-light-fill-inverse group-aria-disabled:text-light-fill-primary-low',
                                    '[&_svg]:absolute [&_svg]:top-0'
                                  )}
                                />
                              </div>
                            )}
                            {v.text?.toLowerCase() == 'videoresource'
                              ? 'Video'
                              : v.text.toLowerCase() == 'calculatorresource'
                              ? 'Calculator'
                              : v.text}
                          </AccordionFacets.ItemLabel>
                        </FacetItem>
                      ))}
                    </AccordionFacets.ValueList>
                  </AccordionFacets.Content>
                )}
              </AccordionFacets.Facet>
            );
          })}
        </SearchResultsAccordionFacets>
      </div>
      {/* Button Controls */}
      <div className="md:hidden w-full p-6 lg:p-8 shadow-small flex items-center justify-between flex-wrap bg-light-background-neutral-variant focus:[&_button]:outline-none">
        <div className="text-center p2-medium text-light-fill-secondary [&_button]:border-none">
          <Button
            label={labels.clearAll}
            fieldClass="!min-w-max !p-0"
            type="auxiliary"
            onClick={() => {
              onClearFilters();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onClearFilters();
              }
            }}
          />
        </div>
        <div className="focus:[&_button]:border-light-stroke-primary-variant-highest">
          <Button
            label={labels.viewResults}
            size="full"
            type="auxiliary"
            onClick={() => setIsFilterPanelVisible(false)}
            onKeyDown={(e) => e.key === 'Enter' && setIsFilterPanelVisible(false)}
          />
        </div>
      </div>
    </div>
  );
};
//#endregion

//#region Card
export const Card = ({
  isCategoryHidden,
  ribbonLabel,
  category_color,
  url,
  image_url,
  category,
  name,
  type,
  description,
  publish_date,
  article_time,
}: CardProps) => {
  const card = tv({
    slots: {
      base: [
        'flex flex-col items-start h-full w-full bg-light-background-neutral-variant',
        'rounded border border-default-3 overflow-hidden',
        'focus:border-light-stroke-primary-variant-highest focus-visible:outline-none',
        'hover:border-light-stroke-primary-low hover:shadow-[0px_2px_40px_0px_rgba(57,100,191,0.15)] hover:no-underline text-inherit hover:text-inherit focus:no-underline',
      ],
      imageContainer: 'w-full min-h-[200px] relative',
      playIconWrapper:
        'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border-4 border-light-stroke-inverse z-10',
      playIcon:
        'absolute inline-block w-5 h-5 text-light-fill-inverse top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
      image: 'w-full max-h-[200px] h-full object-cover object-center',
      content: 'flex flex-col justify-between p-4 w-full h-full',
      categoryStripe: ['after:block', 'after:h-2', 'after:w-full'],
      category: 'mb-1 c1',
      title: 'mb-3 lg:mb-4.5',
      description: 'mb-4 lg:mb-6 text-light-fill-primary-variant p3',
      metaDetails: 'flex items-center justify-between c1',
      arrowIcon: 'w-[18px] h-[18px] lg:w-[22px] lg:h-[22px] text-light-fill-secondary',
    },
    variants: {
      categoryType: {
        'category-1': {
          categoryStripe: 'after:bg-light-other-yourBusiness',
        },
        'category-2': {
          categoryStripe: 'after:bg-light-other-perspectives',
        },
        'category-3': {
          categoryStripe: 'after:bg-light-other-community',
        },
        'category-4': {
          categoryStripe: 'after:bg-light-other-yourMoney',
        },
      },
    },
  });

  const {
    base,
    imageContainer,
    image,
    categoryStripe,
    content,
    category: categoryClasses,
    title,
    description: descriptionClasses,
    metaDetails,
    playIconWrapper,
    playIcon,
    arrowIcon,
  } = card(category_color && { categoryType: category_color?.[0] });

  return (
    <LinkWrapper tabIndex={0} field={{ href: url }} className={base()}>
      <div
        className={classNames(
          imageContainer(),
          categoryStripe(),
          'relative',
          type === 'VideoResource' &&
            'before:absolute before:bg-light-other-ink0/50 before:w-full before:h-full before:top-0 before:left-0'
        )}
      >
        <ImageWrapper
          field={{
            value: { src: image_url },
          }}
          className={image()}
          width="325"
          height="192"
        />
        {type === 'VideoResource' && (
          <div className={playIconWrapper()}>
            <SVG svg="icon-play" hidden={false} className={playIcon()} />
          </div>
        )}

        {ribbonLabel ? (
          <Text
            field={ribbonLabel}
            tag="span"
            class="absolute inline-block bottom-0 left-0 py-1 px-3 bg-light-background-secondary-brand text-light-fill-inverse text-sm"
          />
        ) : null}
      </div>
      {/* Category colored stripe */}

      <div className={content()}>
        <div>
          {!isCategoryHidden && category && category?.length > 0 && (
            <p className={categoryClasses()}>{category[0]}</p>
          )}
          <RichTextA11yWrapper className={title()} tag="h4" field={{ value: name }} />
          <RichTextA11yWrapper className={descriptionClasses()} field={{ value: description }} />
        </div>
        <div className={metaDetails()}>
          <p className="h-full flex items-center justify-center">
            {publish_date && formatTimestamp(publish_date, 'Month DD, YYYY')}
            <span className="inline-block mx-2 w-[1px] h-[calc(100%_-_8px)] bg-light-other-ink" />
            {article_time}
          </p>
          <SVG svg="icon-arrow-right" hidden={false} className={arrowIcon()}></SVG>
        </div>
      </div>
    </LinkWrapper>
  );
};
//#endregion

export const SearchWithFilterWidget = widget(
  (props: SearchWithFilterWidgetProps) => <SearchWithFilter {...props} />,
  WidgetDataType.SEARCH_RESULTS,
  'content'
);
